/* ILS — Inline email forms (footer + homepage). TASK-2368, TASK-2432, TASK-5977,
   TASK-5998, TASK-5999.

   TASK-5999: these two forms are signup surfaces 2 and 3 of three, and they
   now write the SHARED "this browser has already received a promo code" flag
   (window.ILSEmailSession, assets/custom-email-session.js) that the automatic
   promo popup reads before it ever opens. Before this task only the popup kept
   such a flag, and only for its own submits, so a visitor who took their code
   from the footer or from the homepage widget still got the popup pushed at
   them on the next page they opened.

   The flag is written in exactly one condition — a non-empty `code` string
   really came back from /api/popup/promo-code. Never when an address was
   merely typed, never on a validation refusal, never on an HTTP or network
   error, and never on {eligible:false, code:null} (the ineligible /
   already-ordered answers). markIssuedIfCode() below is the whole test, and
   assets/custom-email-popup.js applies the identical one. Nothing else about
   these forms changed: not the markup, not the /contact POST, not the
   TASK-5977 gate, not the FO10/SO05 decision, not the result panel, and not
   one line of the TASK-5998 telemetry.

   TASK-5977: step 0 of the submit handler is now the SHARED email typo /
   confirmation gate (assets/custom-email-verify.js, window.ILSEmailVerify) —
   the same module, the same UI and the same localized strings the popup uses.
   It runs BEFORE the Shopify /contact POST that creates the customer, before
   /api/popup/email-status and before /api/popup/promo-code, so a mistyped
   address never creates a customer, a profile, a code or a letter. The server
   runs the same check authoritatively and answers 409 `email_unconfirmed`;
   that body is handled below and renders the identical panel, which is what
   makes the gate impossible to bypass by simply not loading the asset.
   Converts the two static <form action="/contact#newsletter"> forms into
   AJAX submitters that:
     1. POST to Shopify /contact endpoint (fire-and-forget) so the customer
        is created and the existing Klaviyo sync channel keeps working with
        the source-footer / source-homepage tags added in TASK-2359.
     2. POST to /api/popup/email-status to learn the customer's status.
     3. For every status except existing_multiple_orders, POST to
        /api/popup/promo-code with the resolved <LANG><MARKET> code and
        the discount type implied by the status (FO10 or SO05) to fetch
        a real code + expires_at.
     4. Replace the form container with a result panel via the shared
        window.ILSEmailResult.render (custom-email-result.js) — same 5
        states as the popup, same texts, same copy-to-clipboard.
     5. On any network / non-2xx failure at any step → render the error
        state (state 5, TASK-2432). No silent "pretend it's new" fallback. */
(function () {
  'use strict';

  /* TASK-5998 — funnel telemetry emitter. Fail-silent and order-independent:
     if assets/custom-funnel-telemetry.js has not executed yet the call is
     queued on window.ILSFunnelQ and drained when it does; if it never loads
     at all (404, blocked, throwing) every call here is a no-op and this form
     behaves exactly as it did before TASK-5998. It NEVER sends an address or a
     code value. */
  function ilsFn(stage, outcome, props) {
    try {
      if (window.ILSFunnel && window.ILSFunnel.event) {
        window.ILSFunnel.event(stage, outcome, props);
        return;
      }
      (window.ILSFunnelQ = window.ILSFunnelQ || []).push([stage, outcome, props]);
    } catch (e) {}
  }
  function ilsFnCtx(form, c) {
    try { if (window.ILSFunnel && window.ILSFunnel.ctx) window.ILSFunnel.ctx(form, c); } catch (e) {}
  }
  function ilsFnAttempt(form) {
    try {
      if (window.ILSFunnel && window.ILSFunnel.attempt) return window.ILSFunnel.attempt(form);
    } catch (e) {}
    return null;
  }
  function ilsFnObserve(el, form) {
    try { if (window.ILSFunnel && window.ILSFunnel.observeImpression) window.ILSFunnel.observeImpression(el, form); } catch (e) {}
  }

  var API_BASE = 'https://alert-dedication-production.up.railway.app';
  var STATUS_ENDPOINT = API_BASE + '/api/popup/email-status';
  var PROMO_ENDPOINT = API_BASE + '/api/popup/promo-code';

  function discountTypeFor(status) {
    if (status === 'existing_one_order') return 'SO05';
    return 'FO10';
  }

  /* TASK-5999 — the ONE condition under which an inline form marks this
     browser as "already got a code", which is what stops the automatic popup
     for good. `source` is 'footer' or 'homepage', taken from the container's
     data-ils-inline-source; no address and no code is ever stored.
     Fail-silent on purpose: if assets/custom-email-session.js has not loaded,
     the shopper still sees their code — they just are not recorded, which is
     the pre-5999 behaviour and never a broken page. */
  function markIssuedIfCode(data, source) {
    try {
      if (!data || typeof data.code !== 'string' || !data.code.length) return;
      if (window.ILSEmailSession && window.ILSEmailSession.markIssued) {
        window.ILSEmailSession.markIssued(source === 'homepage' ? 'homepage' : (source === 'footer' ? 'footer' : 'other'));
      }
    } catch (e) {}
  }

  function fetchJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('bad status ' + r.status);
      return r.json();
    });
  }

  // TASK-5839 — ctx carries the raw localization facts the section read from
  // Shopify (country / language / market handle). The backend does not trust
  // the pre-computed combo on its own: it cross-checks it against the country
  // and repairs it, which is what a legacy /en-eu/ -> /en-de/ redirect needs.
  /* TASK-5977 - a 409 from the promo endpoint is NOT an error: it is the
     SERVER asking the same question the shared gate asks, and it is the reason
     the gate cannot be bypassed. fetchJson throws on any non-2xx, so the 409
     body has to be read here, separately, before that happens. */
  function fetchPromo(body) {
    return fetch(PROMO_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (r.status === 409) {
        return r.json().then(function (j) { return { __unconfirmed: j || {} }; });
      }
      if (!r.ok) throw new Error('bad status ' + r.status);
      return r.json();
    });
  }

  function runFlow(email, source, marketCode, ctx, confirmed) {
    /* TASK-5998 — ONE correlation id per interaction, forwarded to every
       endpoint this flow touches, so the server's record of "a request
       arrived / was refused / issued" joins the browser's record of "the form
       was seen, submit was pressed, a code was painted". It is a random UUID
       held in a page-scoped variable: it carries no identity, is never stored
       in a cookie and dies with the page. */
    var attemptId = ilsFnAttempt(source);
    return fetchJson(STATUS_ENDPOINT, { email: email, source: source, attempt_id: attemptId })
      .then(function (statusResp) {
        var s = statusResp && statusResp.status;
        var valid = { 'new': 1, 'existing_no_order': 1, 'existing_one_order': 1, 'existing_multiple_orders': 1 };
        if (!valid[s]) throw new Error('bad status value');
        if (s === 'existing_multiple_orders') return { status: s };
        return fetchPromo({
          email: email,
          source: source,
          attempt_id: attemptId,
          lang_domain: marketCode,
          country: (ctx && ctx.country) || '',
          language: (ctx && ctx.language) || '',
          market_handle: (ctx && ctx.marketHandle) || '',
          /* TASK-5977 - only ever true when the visitor explicitly kept an
             address the gate had questioned. Never defaulted to true. */
          email_confirmed: confirmed === true,
          discount_type: discountTypeFor(s)
        }).then(function (promo) {
          if (promo && promo.__unconfirmed) return { status: 'unconfirmed', unconfirmed: promo.__unconfirmed };
          // TASK-5839 — a 200 no longer implies a code. The backend decides
          // eligibility from Shopify order history for EVERY address, so it
          // can answer {code:null, eligible:false} where the Klaviyo-derived
          // status said 'new'. Rendering that as a success is exactly how
          // REPORT-5836 C-2 produced an empty code and "Valid for 30 days ()".
          if (!promo || !promo.code) {
            return { status: promo && promo.eligible === false ? 'existing_multiple_orders' : 'error' };
          }
          return { status: s, code: promo.code, expires_at: promo.expires_at };
        });
      })
      .catch(function (err) {
        /* TASK-5998 — the shopper is about to see the error panel. Before this
           task that was invisible: a failed request left no row anywhere, so
           "the funnel is healthy" could be said while every request 500'd. */
        ilsFn('client_error', 'network', {
          form: source, now: true,
          reason: String((err && err.message) || 'fetch_failed').slice(0, 60)
        });
        return { status: 'error' };
      });
  }

  // Fire-and-forget POST to Shopify /contact so the customer is created and
  // synced to Klaviyo with the tags the form already carries. We don't
  // block the UI on this — if the network fails the user still sees their
  // code / error render.
  function submitToShopify(form) {
    try {
      var fd = new FormData(form);
      // TASK-3493 — the <form> no longer carries action="/contact" (that made
      // Shopify's invisible reCAPTCHA hijack the on-page submit and navigate the
      // top frame to /challenge). form.action now resolves to the page URL, so we
      // POST to the Shopify contact endpoint explicitly. This is a background fetch
      // — reCAPTCHA only intercepts real navigating form submits, never fetch/XHR —
      // so the customer + Klaviyo source tag are still created, without the bug.
      var endpoint = (form.dataset && form.dataset.ilsContactAction) || '/contact';
      fetch(endpoint, {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'text/html' }
      });
    } catch (e) {}
  }

  function initContainer(container) {
    var form = container.querySelector('form');
    if (!form) return;
    if (form.dataset.ilsInlineBound) return;
    form.dataset.ilsInlineBound = '1';

    var source = (container.getAttribute('data-ils-inline-source') || 'footer').toLowerCase();
    var marketCode = (container.getAttribute('data-ils-market-code') || 'ENEU').toUpperCase();
    // TASK-5839 — the raw facts, forwarded untouched so the server can
    // validate the combo instead of trusting it. Absent on a cached
    // pre-5839 render, in which case the server falls back to honouring
    // the legacy combo rather than refusing the shopper a code.
    var ilsCtx = {
      country: (container.getAttribute('data-ils-country') || '').toUpperCase(),
      language: (container.getAttribute('data-ils-language') || '').toLowerCase(),
      marketHandle: container.getAttribute('data-ils-market-handle') || ''
    };

    /* TASK-5998 — the facts this form already read from Shopify, handed to the
       telemetry once so every later event carries the market without the call
       sites repeating themselves. */
    ilsFnCtx(source, {
      source: source,
      lang_domain: marketCode,
      country: ilsCtx.country || null,
      language: ilsCtx.language || null,
      template: (window.ILS_PAGE && window.ILS_PAGE.template) || null
    });
    /* THE EXPOSURE DENOMINATOR, which did not exist before TASK-5998. "Seen"
       = at least half the form on screen for at least 500 ms, and the submit
       button is hit-tested once at that moment so REPORT-5994 §8's
       "the popup covers the homepage form" becomes a daily number instead of a
       one-off observation. Observation only: nothing about the popup changes. */
    ilsFnObserve(container, source);

    form.addEventListener('submit', function (e) {
      // Confirmation modal on the homepage form (custom-overrides.js
      // initEmailConfirm) intercepts the FIRST submit and calls
      // e.preventDefault() to open the "Is this correct?" dialog. We
      // must not fire the AJAX flow then — we wait for the user's
      // confirmation, at which point form.requestSubmit() re-fires the
      // submit with the modal handler's confirmed flag set, so it
      // returns without preventing. Our handler runs against that
      // second event and proceeds normally. Footer form has no
      // confirmation modal → we always fall through on the first submit.
      if (e.defaultPrevented) return;
      e.preventDefault();

      var input = form.querySelector('input[type="email"]');
      if (!input || !input.value) {
        /* TASK-5998 — a press with an empty field. Counted apart from a real
           attempt, because "pressed with nothing typed" is not a lost lead. */
        ilsFn('form_submit', 'empty_input', { form: source });
        return;
      }
      var email = input.value.trim();
      if (!email) {
        ilsFn('form_submit', 'empty_input', { form: source });
        return;
      }
      /* TASK-5998 — THIS is the number the owner asked for: a real person
         pressed submit. A double-click produces two rows under one
         attempt_id, and every report counts DISTINCT attempt_id, so a
         double-click is one person. */
      ilsFn('form_submit', 'pressed', { form: source, now: true });

      var btn = form.querySelector('button[type="submit"]');
      var btnLabel = btn ? btn.textContent : '';
      /* TASK-2603 — localized "Checking…" from the shared data-ils-eresult-i18n block. */
      function busy() {
        if (btn) { btn.disabled = true; btn.textContent = (function(){try{var _e=document.querySelector('[data-ils-eresult-i18n]');if(_e&&_e.textContent){var _p=JSON.parse(_e.textContent);if(_p&&_p.checking)return _p.checking;}}catch(_x){}return 'Checking…';})(); }
      }
      /* TASK-5977 — the button must come BACK while the confirmation panel is
         open, or a visitor who corrects their address has no way to resubmit. */
      function idle() {
        if (btn) { btn.disabled = false; if (btnLabel) btn.textContent = btnLabel; }
      }
      busy();

      /* ------------------------------------------------------------------
         TASK-5977 — THE SHARED TYPO / CONFIRMATION GATE, FIRST.

         Before submitToShopify (which creates the Shopify customer and the
         Klaviyo source tag), before /api/popup/email-status, before the
         order-count lookup, before FO10/SO05 selection, before issuance and
         before any letter. Nothing is created until the address is settled.

         The gate resolves null while the visitor is still editing — then the
         button is restored and we return, leaving the form exactly as it was.
         A missing / not-yet-loaded asset falls through to the pre-5977
         behaviour rather than blocking the shopper; the server runs the same
         check authoritatively and the 409 branch below asks there instead.
         ------------------------------------------------------------------ */
      var gate = (window.ILSEmailVerify && window.ILSEmailVerify.gate)
        ? window.ILSEmailVerify.gate({ container: container, input: input, email: email, source: source, mount: container, onAsk: idle })
        /* TASK-5998 — the shared typo gate asset did not load. The server runs
           the same check authoritatively, so the shopper is not at risk, but
           a page serving a build without the gate is an operational fact that
           used to be invisible. */
        : (ilsFn('typo_gate', 'skipped', { form: source, reason: 'asset_missing' }),
          Promise.resolve({ email: email, confirmed: false }));

      gate.then(function (settledAddr) {
        if (!settledAddr) { idle(); return; }
        var finalEmail = settledAddr.email;
        var confirmed = settledAddr.confirmed === true;
        busy();

        submitToShopify(form);

        /* TASK-2545 ils-tracker — link this email to the first-party visitor id
           (fire-and-forget; never affects the existing UX). source is 'footer'
           or 'homepage' per the container's data-ils-inline-source. */
        try { if (window.ILSTracker && window.ILSTracker.identify) window.ILSTracker.identify(finalEmail, source); } catch (e) {}

        runFlow(finalEmail, source, marketCode, ilsCtx, confirmed).then(function (data) {
          /* TASK-5977 — the SERVER asked a question the pre-check did not.
             Render the identical panel from the server's own body and, once
             the visitor answers, run the WHOLE flow again for the settled
             address — so the order-count checks and the FO10/SO05 decision are
             made on the address that is actually going to be used. */
          if (data && data.status === 'unconfirmed' && window.ILSEmailVerify && window.ILSEmailVerify.fromServer) {
            idle();
            window.ILSEmailVerify.fromServer({
              onAsk: idle,
              container: container, mount: container, input: input,
              source: source, email: finalEmail, payload: data.unconfirmed
            }).then(function (again) {
              if (!again) return;
              busy();
              submitToShopify(form);
              runFlow(again.email, source, marketCode, ilsCtx, again.confirmed === true)
                .then(function (d2) {
                  markIssuedIfCode(d2, source);
                  window.ILSEmailResult.render(container, (d2 && d2.status === 'unconfirmed') ? { status: 'error' } : d2, { variant: 'inline' });
                });
            });
            return;
          }
          markIssuedIfCode(data, source);
          window.ILSEmailResult.render(container, (data && data.status === 'unconfirmed') ? { status: 'error' } : data, { variant: 'inline' });
        });
      }, function () { idle(); });
    });
  }

  function init() {
    document.querySelectorAll('[data-ils-inline-container]').forEach(initContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
